import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/guides/receive-inbound-email-as-json-nextjs' as const

const TITLE = 'Receive Inbound Email as JSON in Next.js | InboxBridge'
const DESCRIPTION =
  'Learn how to receive incoming emails in a Next.js app as structured JSON using an email webhook, with route handler examples and payload tips.'
const URL = `${siteConfig.siteUrl}/guides/receive-inbound-email-as-json-nextjs`

// Pinned manually so dateModified only changes when the content actually does.
// Bump dateModified when you make substantive edits to the guide body.
const DATE_PUBLISHED = '2026-06-27'
const DATE_MODIFIED = '2026-07-20'

const ARTICLE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline: 'How to Receive Inbound Email as JSON in Next.js',
  description: DESCRIPTION,
  image: siteConfig.siteImageUrl,
  url: URL,
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_MODIFIED,
  author: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
    logo: { '@type': 'ImageObject', url: siteConfig.siteImageUrl },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': URL },
}

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Receive inbound email as JSON in Next.js',
      item: URL,
    },
  ],
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': ARTICLE_JSON_LD },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
  ]
}

const codeBlockClass =
  'overflow-x-auto rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground'

export default function ReceiveInboundEmailAsJsonNextjsGuide() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Guide
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          How to Receive Inbound Email as JSON in Next.js
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Next.js is great for building web apps, dashboards, internal tools, and SaaS
          products. But there is one thing it does not handle on its own: receiving email.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          If you want users, vendors, devices, or internal systems to send email into your
          application, you usually need more than a normal API route. You need a way to
          receive the message, parse it, handle attachments, and send the result to your
          app in a format your code can work with.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          That is where an inbound email webhook helps.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Instead of running mail servers or polling an inbox, you can give people an email
          address. When a message arrives, InboxBridge parses the email and sends it to
          your Next.js app as a normal HTTP POST request with structured JSON.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          In this guide, we will build a simple inbound email endpoint in Next.js that can
          receive incoming email as JSON.
        </p>
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">In this guide</h2>
        <p className="leading-relaxed text-muted-foreground">We will cover:</p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Why receiving email is different from a normal API request</li>
          <li>How to create a Next.js route handler for inbound email</li>
          <li>What an inbound email JSON payload looks like</li>
          <li>How to verify webhook requests</li>
          <li>How to handle attachments</li>
          <li>How to avoid duplicate processing</li>
          <li>How to test the endpoint locally</li>
          <li>A fuller production-style example</li>
        </ul>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">What we are building</h2>
        <p className="leading-relaxed text-muted-foreground">
          We are going to create a Next.js route handler that receives an inbound email
          webhook. The flow looks like this:
        </p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Someone sends an email to your InboxBridge address</li>
          <li>InboxBridge receives and parses the email</li>
          <li>InboxBridge sends a JSON webhook to your Next.js app</li>
          <li>Your app handles the email like any other API request</li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          For example, your app could use the incoming email to:
        </p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Create a support ticket</li>
          <li>Add a reply to an existing conversation</li>
          <li>Trigger an AI workflow</li>
          <li>Send a Slack notification</li>
          <li>Store an attachment</li>
          <li>Create a lead from a contact form fallback</li>
          <li>Process alerts from devices, vendors, or legacy systems</li>
        </ul>
        <p className="leading-relaxed text-muted-foreground">
          The nice part is that your application does not need to speak SMTP. It just needs
          to handle JSON.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Why receiving email is different from receiving an API request
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Most web apps are built around HTTP. A user clicks a button, submits a form, or
          sends an API request. Your server receives that request and responds.
        </p>
        <p className="leading-relaxed text-muted-foreground">Email works differently.</p>
        <p className="leading-relaxed text-muted-foreground">
          Email is delivered through mail servers using protocols like SMTP. A raw email
          message can include headers, plain text, HTML, replies, forwards, attachments,
          inline images, spam metadata, and more.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          If you try to build this yourself, you may need to deal with MX records, SMTP
          servers, MIME parsing, attachments, email headers, spam filtering, retry
          behavior, bounced messages, inbound routing, local development testing, and
          logging for failed deliveries.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          For some products, building that infrastructure makes sense. For many SaaS apps,
          internal tools, side projects, and automation workflows, it is more work than the
          feature is worth.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          A webhook keeps it simple. Email comes in. JSON goes out.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Create a Next.js route handler
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          In a Next.js app using the App Router, create this file:
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { NextResponse } from "next/server";

export async function POST(request: Request) {
  const email = await request.json();

  console.log("New inbound email received");
  console.log("From:", email.from.email);
  console.log("To:", email.to[0]?.email);
  console.log("Subject:", email.subject);
  console.log("Text:", email.textBody);

  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          This gives you a public endpoint at <code>/api/inbound-email</code>. If your app
          is deployed at <code>https://example.com</code>, the full webhook URL would be:
        </p>
        <pre className={codeBlockClass}>
          {`https://example.com/api/inbound-email`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          That is the URL you will use in InboxBridge.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Test the route with curl</h2>
        <p className="leading-relaxed text-muted-foreground">
          Before connecting InboxBridge, you can test the route with a local POST request.
        </p>
        <pre className={codeBlockClass}>
          {`curl -X POST http://localhost:3000/api/inbound-email \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": { "email": "customer@example.com", "name": "Jordan Lee" },
    "to": [{ "email": "abc123@webhook.inboxbridge.io", "name": "" }],
    "subject": "Test email",
    "textBody": "Hello from a test payload",
    "htmlBody": "<p>Hello from a test payload</p>",
    "attachments": []
  }'`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          If the route is working, you should see the sender, recipient, subject, and body
          logged by your Next.js app.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Example inbound email payload
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          When an email is received, your endpoint gets a JSON payload. InboxBridge posts a
          parsed message that looks like this:
        </p>
        <pre className={codeBlockClass}>
          {`{
  "id": "9b2c1f7a4e",
  "receivedAt": "2026-06-27T15:04:22.000Z",
  "from": { "email": "customer@example.com", "name": "Jordan Lee" },
  "to": [{ "email": "abc123@webhook.inboxbridge.io", "name": "" }],
  "cc": [],
  "bcc": [],
  "replyTo": null,
  "subject": "Question about my order",
  "date": "2026-06-27T15:04:00.000Z",
  "textBody": "Hey, can someone check order #1234?",
  "htmlBody": "<p>Hey, can someone check order #1234?</p>",
  "attachments": []
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          This is much easier to work with than a raw email message. The sender and
          recipients are objects with an <code>email</code> and a <code>name</code>, and the
          body is split into <code>textBody</code> and <code>htmlBody</code>.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          You can read the values directly from JavaScript:
        </p>
        <pre className={codeBlockClass}>
          {`const sender = email.from.email;
const subject = email.subject;
const message = email.textBody;
const attachments = email.attachments;`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          From there, you can store it in a database, pass it to a queue, send it to another
          API, or trigger whatever workflow your app needs.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Want to test this with a real email?{' '}
          <Link
            to={LOGIN_PATH}
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            Create a free InboxBridge bridge
          </Link>
          , paste in your Next.js webhook URL, and send a message to the generated address.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Verify the request</h2>
        <p className="leading-relaxed text-muted-foreground">
          Your webhook endpoint will be public, so you should confirm that a request really
          came from InboxBridge before you act on it.
        </p>
        <h3 className="text-xl font-semibold text-primary">A secret URL token</h3>
        <p className="leading-relaxed text-muted-foreground">
          The simplest starting point is to include a secret token in your webhook URL:
        </p>
        <pre className={codeBlockClass}>
          {`https://example.com/api/inbound-email?secret=YOUR_LONG_RANDOM_SECRET`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Then check that secret inside the route handler:
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { NextRequest, NextResponse } from "next/server";

export async function POST(request: NextRequest) {
  const secret = request.nextUrl.searchParams.get("secret");

  if (secret !== process.env.INBOUND_EMAIL_SECRET) {
    return NextResponse.json({ error: "Unauthorized" }, { status: 401 });
  }

  const email = await request.json();

  console.log("Inbound email:", {
    from: email.from.email,
    to: email.to[0]?.email,
    subject: email.subject,
  });

  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Use a long, random secret. Do not use something easy to guess.
        </p>
        <h3 className="text-xl font-semibold text-primary">
          Verify the signature InboxBridge sends
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          InboxBridge signs every delivery, so you can verify it instead of relying on a URL
          token alone. Each request includes an <code>X-InboxBridge-Signature</code> header
          in the form <code>sha256=...</code> and an <code>X-InboxBridge-Timestamp</code>{' '}
          header. The signature is an HMAC-SHA256 of the raw request body, using the signing
          secret InboxBridge generates for your bridge.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          The simplest way is our open-source package,{' '}
          <a
            href="https://www.npmjs.com/package/inbound-email-webhook"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            inbound-email-webhook
          </a>
          . It reads the raw body, checks the signature with a constant-time compare, and
          returns a typed email. It is the same code InboxBridge uses in production.
        </p>
        <pre className={codeBlockClass}>{`npm install inbound-email-webhook`}</pre>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { verifyRequest } from "inbound-email-webhook/next";

export const runtime = "nodejs";

export async function POST(request: Request) {
  const result = await verifyRequest(request, {
    secret: process.env.INBOXBRIDGE_SIGNING_SECRET!,
  });

  if (!result.ok) return result.response;

  const email = result.email; // typed InboundEmail
  console.log("Inbound email:", {
    from: email.from.email,
    subject: email.subject,
  });

  return Response.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          The package also checks the <code>X-InboxBridge-Timestamp</code> header for freshness
          when you pass a <code>toleranceSeconds</code> option, which reduces replay risk.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Prefer not to add a dependency? You can verify the signature by hand. Compute the
          same HMAC over the raw body and compare:
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import crypto from "crypto";
import { NextRequest, NextResponse } from "next/server";

export const runtime = "nodejs";

export async function POST(request: NextRequest) {
  const signature = request.headers.get("x-inboxbridge-signature") ?? "";
  const body = await request.text();

  const expected =
    "sha256=" +
    crypto
      .createHmac("sha256", process.env.INBOXBRIDGE_SIGNING_SECRET!)
      .update(body)
      .digest("hex");

  const valid =
    signature.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));

  if (!valid) {
    return NextResponse.json({ error: "Invalid signature" }, { status: 401 });
  }

  const email = JSON.parse(body);

  // handle the email
  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Read the body with <code>request.text()</code> and verify the signature before you
          call <code>JSON.parse</code>, since the HMAC is computed over the exact bytes that
          were sent.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          This example uses Node&apos;s <code>crypto</code> module, so it assumes the default
          Node.js runtime for the route handler. If you deploy this route to the Edge runtime,
          use Web Crypto instead.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          InboxBridge also sends an <code>X-InboxBridge-Timestamp</code> header. In production,
          you should also reject old timestamps if your webhook signing format supports it.
          That helps reduce replay risk.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Store the inbound email</h2>
        <p className="leading-relaxed text-muted-foreground">
          Logging the email is enough for a first test, but most real applications will
          store the message. Here is a simple example using pseudo database code:
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { NextRequest, NextResponse } from "next/server";
import { db } from "@/lib/db";

export async function POST(request: NextRequest) {
  const secret = request.nextUrl.searchParams.get("secret");

  if (secret !== process.env.INBOUND_EMAIL_SECRET) {
    return NextResponse.json({ error: "Unauthorized" }, { status: 401 });
  }

  const email = await request.json();

  await db.inboundEmail.create({
    data: {
      messageId: email.id,
      from: email.from.email,
      to: email.to[0]?.email,
      subject: email.subject,
      text: email.textBody,
      html: email.htmlBody,
      rawPayload: email,
    },
  });

  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          The <code>db</code> import is just an example. Replace it with Prisma, Drizzle,
          Supabase, PlanetScale, Postgres, MySQL, or whatever you use in your app.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          For production, it is often better to store the email quickly and process it in
          the background. Webhooks should respond fast. If your endpoint takes too long, the
          sending service may treat the delivery as failed.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Create a support ticket from an email
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          One of the most common inbound email use cases is support. A customer sends an
          email, your app receives the webhook, and then you create a ticket.
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { NextRequest, NextResponse } from "next/server";
import { db } from "@/lib/db";

export async function POST(request: NextRequest) {
  const secret = request.nextUrl.searchParams.get("secret");

  if (secret !== process.env.INBOUND_EMAIL_SECRET) {
    return NextResponse.json({ error: "Unauthorized" }, { status: 401 });
  }

  const email = await request.json();

  const ticket = await db.ticket.create({
    data: {
      requesterEmail: email.from.email,
      title: email.subject || "New email request",
      description: email.textBody || "",
      source: "email",
      status: "open",
      rawEmail: email,
    },
  });

  return NextResponse.json({
    ok: true,
    ticketId: ticket.id,
  });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          This is the basic pattern behind many reply-by-email and helpdesk workflows. The
          email address becomes an input to your product.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Trigger an AI workflow from an inbound email
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Another common use case is sending email into an AI workflow. For example, you may
          want to classify an incoming email as a sales lead, support request, billing
          question, bug report, vendor alert, or spam.
        </p>
        <p className="leading-relaxed text-muted-foreground">A simple version might look like this:</p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { NextRequest, NextResponse } from "next/server";
import { classifyEmail } from "@/lib/classify-email";
import { db } from "@/lib/db";

export async function POST(request: NextRequest) {
  const secret = request.nextUrl.searchParams.get("secret");

  if (secret !== process.env.INBOUND_EMAIL_SECRET) {
    return NextResponse.json({ error: "Unauthorized" }, { status: 401 });
  }

  const email = await request.json();

  const classification = await classifyEmail({
    from: email.from.email,
    subject: email.subject,
    text: email.textBody,
  });

  await db.inboundEmail.create({
    data: {
      messageId: email.id,
      from: email.from.email,
      subject: email.subject,
      text: email.textBody,
      category: classification.category,
      rawPayload: email,
    },
  });

  return NextResponse.json({ ok: true });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          For a small app, this may be enough. For a larger app, you may want to enqueue the
          message and run the AI step in a background job. That keeps the webhook response
          fast and avoids timeouts.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Handle attachments</h2>
        <p className="leading-relaxed text-muted-foreground">
          Inbound email often includes attachments. These might be PDFs, images, CSV files,
          invoices, signed forms, or screenshots. InboxBridge includes an{' '}
          <code>attachments</code> array that describes each file with its name, content
          type, and size. On paid plans, each entry also carries a secure, time-limited{' '}
          <code>downloadUrl</code> — the raw bytes are stored for you and fetched on demand,
          so your webhook payloads stay small.
        </p>
        <pre className={codeBlockClass}>
          {`{
  "from": { "email": "vendor@example.com", "name": "Acme Billing" },
  "to": [{ "email": "abc123@webhook.inboxbridge.io", "name": "" }],
  "subject": "Invoice for June",
  "textBody": "Invoice attached.",
  "attachments": [
    {
      "name": "invoice-june.pdf",
      "contentType": "application/pdf",
      "contentLength": 482931,
      "contentId": null,
      "downloadUrl": "https://inboxbridge.io/attachments/clx1a2b3?token=…"
    }
  ]
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Your app can loop through the attachments and store the metadata:
        </p>
        <pre className={codeBlockClass}>
          {`for (const attachment of email.attachments || []) {
  await db.emailAttachment.create({
    data: {
      filename: attachment.name,
      contentType: attachment.contentType,
      size: attachment.contentLength,
    },
  });

  // Paid plans include a signed downloadUrl — pull the bytes when you need them.
  if (attachment.downloadUrl) {
    const res = await fetch(attachment.downloadUrl);
    const bytes = Buffer.from(await res.arrayBuffer());
    // ...persist \`bytes\` to your own durable storage (S3, R2, etc.)
  }
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          On paid plans, InboxBridge stores each attachment and includes a signed{' '}
          <code>downloadUrl</code> that stays valid for your plan&rsquo;s retention window —
          fetch the bytes when you need them and re-store them somewhere durable like S3. On
          the free plan the payload carries attachment metadata only: the file name, content
          type, and size, but no bytes. Either way, scan, size-limit, and restrict file types
          before trusting any file.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Return the right status code</h2>
        <p className="leading-relaxed text-muted-foreground">
          Your route handler should return a <code>200</code> response when the email was
          accepted.
        </p>
        <pre className={codeBlockClass}>{`return NextResponse.json({ ok: true });`}</pre>
        <p className="leading-relaxed text-muted-foreground">
          If your endpoint returns a <code>500</code>, times out, or fails to respond, the
          delivery may be treated as failed. That can be useful when something is truly
          broken. For example, if your database is down, returning an error tells InboxBridge
          to retry the message later. But avoid doing slow work inside the request if you can
          help it.
        </p>
        <p className="leading-relaxed text-muted-foreground">A good production pattern is:</p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Validate the request</li>
          <li>Store the raw email payload</li>
          <li>Queue any slow processing</li>
          <li>
            Return <code>200 OK</code>
          </li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          Then your background worker can handle AI classification, Slack notifications,
          ticket creation, attachment processing, or other longer-running tasks.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Avoid duplicate processing</h2>
        <p className="leading-relaxed text-muted-foreground">
          InboxBridge retries failed deliveries with exponential backoff, which is useful but
          introduces one thing you need to handle: the same message can arrive more than
          once. If your endpoint fails after saving the email but before responding, the same
          webhook could be delivered again.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          The fix is to use an idempotency key. Every InboxBridge payload includes a stable{' '}
          <code>id</code>, so you can store it and enforce uniqueness in your database.
        </p>
        <pre className={codeBlockClass}>
          {`const messageId = email.id;

const existingEmail = await db.inboundEmail.findUnique({
  where: { messageId },
});

if (existingEmail) {
  return NextResponse.json({ ok: true, duplicate: true });
}

await db.inboundEmail.create({
  data: {
    messageId,
    from: email.from.email,
    subject: email.subject,
    text: email.textBody,
    rawPayload: email,
  },
});`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          This way, retrying the webhook does not create duplicate tickets, duplicate Slack
          messages, or duplicate AI runs.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Test locally</h2>
        <p className="leading-relaxed text-muted-foreground">
          If your Next.js app is running on your machine, InboxBridge cannot reach{' '}
          <code>localhost</code> directly. You need a public URL that forwards traffic to
          your local app. Tools like ngrok, Cloudflare Tunnel, or similar tunneling tools can
          help with this.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          For example, if your Next.js app is running on port <code>3000</code>, your local
          endpoint is:
        </p>
        <pre className={codeBlockClass}>{`http://localhost:3000/api/inbound-email`}</pre>
        <p className="leading-relaxed text-muted-foreground">
          Your public tunnel URL might look like:
        </p>
        <pre className={codeBlockClass}>
          {`https://your-tunnel-url.ngrok-free.app/api/inbound-email`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Use the public tunnel URL as your InboxBridge webhook URL while testing. Once your
          app is deployed, you can replace it with your production URL.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Set up InboxBridge</h2>
        <p className="leading-relaxed text-muted-foreground">
          After your route handler is ready, create a new bridge in InboxBridge. The setup is
          simple:
        </p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Create a bridge</li>
          <li>Paste your webhook URL</li>
          <li>
            Copy your generated InboxBridge email address, which looks like{' '}
            <code>abc123@webhook.inboxbridge.io</code>
          </li>
          <li>Send a test email to that address</li>
          <li>Watch the webhook request arrive in your app</li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          Your webhook URL should look something like this:
        </p>
        <pre className={codeBlockClass}>
          {`https://example.com/api/inbound-email?secret=YOUR_LONG_RANDOM_SECRET`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          When an email arrives, InboxBridge sends the parsed message to your Next.js route
          handler as JSON. Every delivery is recorded with its response code and duration, so
          you can inspect what happened, retry failures with exponential backoff, and replay
          any message from the bridge page when needed.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          When to use an email webhook instead of polling an inbox
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          You can build inbound email features by connecting to Gmail or another inbox over
          IMAP. That approach can work, but it comes with tradeoffs. Polling an inbox usually
          means:
        </p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Running a scheduled job</li>
          <li>Managing OAuth or mailbox credentials</li>
          <li>Waiting for the next polling interval</li>
          <li>Handling provider-specific behavior</li>
          <li>Tracking which messages have already been processed</li>
          <li>Dealing with folder state, labels, read status, and deletion</li>
        </ul>
        <p className="leading-relaxed text-muted-foreground">
          A webhook is simpler for application workflows. The email arrives, and your app
          gets an HTTP request. That makes webhooks a better fit for things like support
          ticket creation, reply-by-email features, AI agents, internal automations, device
          alerts, vendor notifications, lead intake, and form fallback addresses.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          If the email is meant to become an event inside your app, a webhook is usually the
          cleaner model.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Common mistakes</h2>

        <h3 className="text-xl font-semibold text-primary">
          Doing too much work inside the webhook
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Keep the webhook fast. Store the email, then process it in the background if the
          work is slow.
        </p>

        <h3 className="text-xl font-semibold text-primary">Not storing the raw payload</h3>
        <p className="leading-relaxed text-muted-foreground">
          Store the original JSON payload. It makes debugging much easier later.
        </p>

        <h3 className="text-xl font-semibold text-primary">Ignoring duplicate deliveries</h3>
        <p className="leading-relaxed text-muted-foreground">
          Retries can happen. Use the payload <code>id</code> or another idempotency key.
        </p>

        <h3 className="text-xl font-semibold text-primary">Forgetting about attachments</h3>
        <p className="leading-relaxed text-muted-foreground">
          Even if your first version only needs text, attachments may matter later. Store
          attachment metadata from the beginning.
        </p>

        <h3 className="text-xl font-semibold text-primary">Using a weak secret</h3>
        <p className="leading-relaxed text-muted-foreground">
          Do not use <code>test</code>, <code>secret</code>, or your app name as the webhook
          secret. Use a long random value.
        </p>

        <h3 className="text-xl font-semibold text-primary">Not testing failed delivery</h3>
        <p className="leading-relaxed text-muted-foreground">
          Test what happens when your endpoint returns an error. You should know how retries,
          logs, and replay work before you need them.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">Full example</h2>
        <p className="leading-relaxed text-muted-foreground">
          Here is a more complete route handler you can use as a starting point.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          The full example below uses a URL token because it is easy to read in a guide. For
          production, use InboxBridge signature verification if it is available for your
          account.
        </p>
        <pre className={codeBlockClass}>
          {`// app/api/inbound-email/route.ts

import { NextRequest, NextResponse } from "next/server";
import { db } from "@/lib/db";

export async function POST(request: NextRequest) {
  const secret = request.nextUrl.searchParams.get("secret");

  if (secret !== process.env.INBOUND_EMAIL_SECRET) {
    return NextResponse.json({ error: "Unauthorized" }, { status: 401 });
  }

  let email;

  try {
    email = await request.json();
  } catch {
    return NextResponse.json({ error: "Invalid JSON" }, { status: 400 });
  }

  const messageId = email.id ?? null;

  if (messageId) {
    const existingEmail = await db.inboundEmail.findUnique({
      where: { messageId },
    });

    if (existingEmail) {
      return NextResponse.json({ ok: true, duplicate: true });
    }
  }

  const savedEmail = await db.inboundEmail.create({
    data: {
      messageId,
      from: email.from.email,
      to: email.to[0]?.email ?? null,
      subject: email.subject || "",
      text: email.textBody || "",
      html: email.htmlBody || "",
      rawPayload: email,
    },
  });

  for (const attachment of email.attachments || []) {
    await db.emailAttachment.create({
      data: {
        inboundEmailId: savedEmail.id,
        filename: attachment.name,
        contentType: attachment.contentType,
        size: attachment.contentLength,
      },
    });
  }

  return NextResponse.json({
    ok: true,
    inboundEmailId: savedEmail.id,
  });
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          This example does a few important things:
        </p>
        <ul className="ml-5 flex list-disc flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>Validates a secret token</li>
          <li>Parses the JSON payload</li>
          <li>Stores the raw email</li>
          <li>Avoids duplicate processing using the payload id</li>
          <li>Stores attachment metadata</li>
          <li>Returns a successful JSON response</li>
        </ul>
        <p className="leading-relaxed text-muted-foreground">
          You can extend this with ticket creation, Slack alerts, AI classification, CRM
          updates, or any other workflow your app needs.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Receive your first inbound email as JSON
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Next.js does not need to know how email works. Your app just needs a webhook
          endpoint. InboxBridge gives you an inbound email address, parses incoming messages,
          and sends them to your application as structured JSON. You can use that to build
          support flows, reply-by-email features, AI inboxes, internal automations, and alert
          pipelines without running mail infrastructure.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          Create a bridge, point it at your Next.js route handler, and send a test email.
          Your app can start receiving email as JSON in minutes.
        </p>
      </section>

      <div className="flex flex-wrap gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Create your first bridge
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/email-to-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          Email to webhook overview
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/guides/inbound-email-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Inbound email webhook tutorial
            </Link>
            . The five-minute version that is not tied to a framework.
          </li>
          <li>
            <Link
              to="/guides/forward-email-to-slack-without-zapier"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Forward email to Slack (without Zapier)
            </Link>
            . A full build that posts formatted, filtered email into a Slack channel.
          </li>
          <li>
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Email to webhook overview
            </Link>
            . What the service does and what payloads look like.
          </li>
          <li>
            <Link
              to="/cloudmailin-alternative"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              CloudMailin alternative
            </Link>
            . Feature comparison if you are evaluating providers.
          </li>
          <li>
            <Link
              to="/"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              InboxBridge home
            </Link>
            . An overview of turning inbound email into webhooks.
          </li>
        </ul>
      </section>
    </main>
  )
}
